import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment";
import Spinner from "../../../../../components/shared/Spinner";
import CellRender from "./CellRender";
import MoreModalDragLayer from "./CellRender/Content/MoreModal/DragLayer";
import Menu from "../../components/Menu";
import MenuContent from "./MenuContent";
import Pagination from "../../components/Pagination";
import { useScheduleContext } from "../../hooks/context/useScheduleContext";
import { useFilters } from "../../../../../utils/hooks/useFilters";
import { useDragAndDropCopyModifier } from "../../hooks/useDragAndDropCopyModifier";
import { useScheduleApi } from "../../api/useScheduleApi";
import { getOrderedWeekdays } from "../../helpers/getOrderedWeekdays";
import { cn } from "../../../../../lib/utils";

function Calendar({
    openActionsModal,
    openCopyModal,
    openDeleteModal,
    openAssignModal,
    openProfileSheet,
    openTransferModal,
    openShiftTradeDrawer,
    openClaimShiftDrawer,
    openSaveDayAsTemplate,
    openLoadTemplatesModal,
    openMoveToOpenShiftsModal
}: any) {
    const { t } = useTranslation();
    const [filters] = useFilters();
    const { type, loading, loadingUnassignedEvents } = useScheduleContext();
    const { getUnassignedShifts } = useScheduleApi();
    const weekdays = getOrderedWeekdays();

    useDragAndDropCopyModifier();

    const { startOfMonth, endOfMonth } = useMemo(() => {
        const startOfMonth = moment(filters?.from).clone().startOf("week");
        const endOfMonth = moment(filters?.to).clone().endOf("week");
        return { startOfMonth, endOfMonth };
    }, [filters?.from, filters?.to]);

    const days = useMemo(() => {
        const _days = [];
        const numDays = endOfMonth.diff(startOfMonth, "days") + 1;
        for (let i = 0; i < numDays; i++) {
            _days.push(startOfMonth.clone().add(i, "days"));
        }
        return _days;
    }, [startOfMonth, endOfMonth]);

    useEffect(() => {
        if (type !== "team") return;
        const controller = new AbortController();
        getUnassignedShifts(filters, controller);
        return () => controller.abort();
    }, [getUnassignedShifts, filters, type]);

    const numWeeks = days.length / 7;

    return (
        <div
            className="tw-w-full tw-mx-auto tw-py-3 tw-flex tw-flex-col"
            // Same measured height as the grid view (topbar + action bar subtracted), so the page itself doesn't scroll
            style={{ height: "var(--schedule-content-height)" }}
        >
            <div
                id="printable-schedule-calendar-content"
                className="tw-flex-1 tw-min-h-0 tw-overflow-x-auto tw-overflow-y-hidden tw-flex tw-flex-col"
            >
                <div className="tw-flex tw-flex-col tw-h-full tw-min-w-[900px]">
                    <div
                        className="tw-grid tw-text-center tw-text-neutral-900 tw-border-neutral-100 tw-bg-white tw-z-10"
                        style={{ gridTemplateColumns: "repeat(7, minmax(120px, 1fr))" }}
                    >
                        {weekdays.map((day, i) => (
                            <div
                                key={day}
                                className={cn(
                                    "tw-p-3 tw-border-r tw-border-t tw-border-b tw-border-neutral-100",
                                    i === 0 && "tw-rounded-tl-md tw-border-l",
                                    i === weekdays.length - 1 && "tw-rounded-tr-md"
                                )}
                            >
                                {t(day)}
                            </div>
                        ))}
                    </div>

                    {(loading || loadingUnassignedEvents) ? (
                        <div className="tw-flex-1 tw-flex tw-items-center tw-justify-center tw-border tw-border-t-0 tw-border-neutral-100 tw-rounded-b-md">
                            <Spinner />
                        </div>
                    ) : (
                        <div
                            className="tw-grid tw-flex-1 tw-border-l tw-border-neutral-100"
                            style={{
                                gridTemplateColumns: "repeat(7, minmax(120px, 1fr))",
                                // minmax(0, 1fr) lets rows shrink below their content on short screens instead of overflowing
                                gridTemplateRows: `repeat(${numWeeks}, minmax(0, 1fr))`,
                            }}
                        >
                            {days.map((day, i) => {
                                const isCurrentMonth = day.isSameOrAfter(moment(filters?.from).startOf("month")) &&
                                    day.isSameOrBefore(moment(filters?.from).endOf("month"));
                                return (
                                    <CellRender
                                        key={day.format("YYYY-MM-DD")}
                                        index={i}
                                        isCurrentMonth={isCurrentMonth}
                                        day={day}
                                        totalDays={days.length}
                                    />
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>

            {(["team", "template"].includes(type) && filters?.mode !== "month") ? <Pagination /> : null}

            <MoreModalDragLayer />

            <Menu>
                <MenuContent
                    openActionsModal={openActionsModal}
                    openCopyModal={openCopyModal}
                    openDeleteModal={openDeleteModal}
                    openAssignModal={openAssignModal}
                    openProfileSheet={openProfileSheet}
                    openTransferModal={openTransferModal}
                    openShiftTradeDrawer={openShiftTradeDrawer}
                    openClaimShiftDrawer={openClaimShiftDrawer}
                    openSaveDayAsTemplate={openSaveDayAsTemplate}
                    openLoadTemplatesModal={openLoadTemplatesModal}
                    openMoveToOpenShiftsModal={openMoveToOpenShiftsModal}
                />
            </Menu>
        </div>
    );
};

export default Calendar;
